import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Pause, Play } from "lucide-react";
import React from "react";

interface VoicePreviewButtonProps {
  previewUrl: string | undefined;
  label?: string;
  className?: string;
}

const activePreviewEvents = new EventTarget();

export default function VoicePreviewButton({
  previewUrl,
  label = "Preview",
  className,
}: VoicePreviewButtonProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const handleOtherPreviewStarted = (triggeredEvent: Event) => {
      const detail = (triggeredEvent as CustomEvent).detail;
      if (detail !== audioRef.current) {
        audioRef.current?.pause();
        setIsPlaying(false);
      }
    };
    activePreviewEvents.addEventListener("preview-started", handleOtherPreviewStarted);
    return () => {
      activePreviewEvents.removeEventListener("preview-started", handleOtherPreviewStarted);
      audioRef.current?.pause();
    };
  }, []);

  if (!previewUrl) {
    return null;
  }

  const togglePlayback = (clickEvent: React.MouseEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    if (!audioRef.current || audioRef.current.src !== previewUrl) {
      audioRef.current?.pause();
      audioRef.current = new Audio(previewUrl);
      audioRef.current.addEventListener("ended", () => setIsPlaying(false));
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));
      setIsPlaying(false);
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      activePreviewEvents.dispatchEvent(
        new CustomEvent("preview-started", { detail: audioRef.current }),
      );
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  };

  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className={className}
      onClick={togglePlayback}
      // Stop pointer events from bubbling to a parent Radix Select.Item, which would otherwise
      // select the voice (and close the dropdown) when the user only wanted to preview it.
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      aria-label={isPlaying ? `Pause ${label}` : `Play ${label}`}
    >
      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
    </Button>
  );
}
